import { Avatar as MuiAvatar, Box, CircularProgress } from '@mui/material';
import { useDataProvider, useRecordContext } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';

import { useEffect, useRef, useState } from 'react';
import { User } from '../resources/chat/types';

export const Avatar = (props: { record?: Pick<User, 'name' | 'avatarUrl'>; rest?: any }) => {
  const record = useRecordContext<Pick<User, 'name' | 'avatarUrl'>>(props);
  const [src, setSrc] = useState<string | undefined>(record.avatarUrl);

  const accessToken = localStorage.getItem('accessToken');

  useEffect(() => {
    setSrc(record.avatarUrl);
  }, [record]);

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
    if (src && new RegExp(`${apiUrl}/files/[0-9]*/content`).test(src)) {
      fetch(src, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
      }).then(response => {
        const data = response.url;
        setSrc(data);
      });
    }
  }, [src]);

  if (!record) return null;

  return (
    <MuiAvatar
      src={src}
      sx={{
        bgcolor: src ? undefined : '#5E636F',
        color: '#fff',
        fontSize: props.rest?.fontSize ?? 14,
        width: props.rest?.size,
        height: props.rest?.size,
        position: props.rest?.position,
        border: props.rest?.border,
        top: props.rest?.top,
        left: props.rest?.left,
        right: props.rest?.right,
        bottom: props.rest?.right,
        zIndex: props.rest?.zIndex,
      }}
    >
      {record.name.charAt(0).toUpperCase()}
      {/* First name is mandatory - no check required. */}
      {/*{record.lastName.charAt(0).toUpperCase()}*/}
      {/* Last name is mandatory - no check required. */}
    </MuiAvatar>
  );
};

export const AvatarGroup = (props: {
  records?: Pick<User, 'name' | 'avatarUrl'>[];
  rest?: any;
}) => {
  const { records } = props;
  if (!records || records.length === 0) return null;

  return (
    <Box {...props.rest}>
      {records.length === 1 && <Avatar record={records[0]} />}
      {records.length === 2 && (
        <Box position="relative" overflow="hidden" width="40px" height="40px">
          <Avatar
            record={records[0]}
            rest={{
              fontSize: 12,
              position: 'absolute',
              size: '25px',
            }}
          />

          <Avatar
            record={records[1]}
            rest={{
              fontSize: 12,
              border: '1px solid #fff',
              position: 'absolute',
              top: '15px',
              left: '15px',
              size: '25px',
              zIndex: 1,
            }}
          />
        </Box>
      )}
      {records.length > 2 && (
        <Box position="relative" overflow="hidden" width="40px" height="40px">
          <Avatar
            record={records[0]}
            rest={{
              fontSize: 12,
              position: 'absolute',
              size: '25px',
            }}
          />

          <Box
            sx={{
              bgcolor: '#5E636F',
              color: '#fff',
              fontSize: 10,
              border: '1px solid #fff',
              position: 'absolute',
              top: '15px',
              left: '15px',
              width: '25px',
              height: '25px',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 100,
            }}
          >
            +{records.length - 1}
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface EditableAvatarProps {
  avatarUrl: string;
  setAvatarUrl: (url: string) => void;
}

export const EditableAvatar = (props: EditableAvatarProps) => {
  const { avatarUrl, setAvatarUrl } = props;

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<any>(null);

  const dataProvider = useDataProvider();
  const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';

  const processFile = async (file: any) => {
    setUploading(true);

    const fileName = uuidv4();
    const res = await dataProvider.create('files', {
      data: {
        name: fileName,
        isPublic: false,
        folder: 'avatars',
        description: file.name,
        file: file,
      },
    });
    if (res && res.data) {
      setAvatarUrl(`${apiUrl}/files/${res.data.id}/content`);
    }
    setUploading(false);
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      processFile(event.dataTransfer.files[0]);
    }
  };

  const handleDragLeave = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleFileChange = (event: any) => {
    event.preventDefault();
    if (event.target.files && event.target.files[0]) {
      processFile(event.target.files[0]);
    }
  };

  const openFileExplorer = (event: any) => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  return (
    <>
      {uploading && (
        <Box
          border="1px dashed #DDD"
          borderRadius={100}
          width={50}
          height={50}
          bgcolor="#fafafa"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress sx={{ p: 1.5 }} />
        </Box>
      )}
      {!uploading && (
        <Box
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={openFileExplorer}
          position="relative"
          sx={{ '&:hover': { cursor: 'pointer' } }}
        >
          <form onSubmit={e => e.preventDefault()}>
            <input
              id="dropzone-avatar"
              style={{ display: 'none' }}
              ref={inputRef}
              type="file"
              multiple={false}
              onChange={handleFileChange}
              accept="image/*;capture=camera"
            />
            {avatarUrl ? (
              <Avatar record={{ avatarUrl: avatarUrl, name: '' }} rest={{ size: 50 }} />
            ) : (
              <Box
                border="1px dashed #DDD"
                borderRadius={100}
                width={50}
                height={50}
                bgcolor={'#fafafa'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                fontSize={10}
                fontWeight={500}
                color="#AAA"
              >
                Add Avatar
              </Box>
            )}

            {dragActive && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(230, 230, 230, 0.75)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid #DDD',
                  borderRadius: 100,
                  width: 50,
                  height: 50,
                }}
              ></div>
            )}
          </form>
        </Box>
      )}
    </>
  );
};
